<script lang="ts" setup>
import { type Component, computed, markRaw, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useIonRouter } from '@ionic/vue';
import { LINKS } from '@/utils/links';
import { useTutorial } from '@/composables/useTutorial';
import { useTutorialFacturation } from '@/composables/useTutorialFacturation';
import BaseButton from '@/atoms/BaseButton.vue';
import LocalIcon from '@/atoms/LocalIcon.vue';
import TutorialDemoInvoiceSign from '@/atoms/tutorial/TutorialDemoInvoiceSign.vue';

const DEMO_REGISTRY: Record<string, Component> = {
  'invoice-sign': markRaw(TutorialDemoInvoiceSign),
};

const main = useTutorial();
const facturation = useTutorialFacturation();
const router = useRouter();
const ionRouter = useIonRouter();

// Use whichever tutorial is currently active
const active = computed(() =>
  main.isActive.value ? main : facturation.isActive.value ? facturation : null,
);

const isAnyActive = computed(() => !!active.value);

const originRoute = ref<string | null>(null);

watch(isAnyActive, (nowActive) => {
  if (nowActive && !originRoute.value) {
    originRoute.value = router.currentRoute.value.fullPath;
  } else if (!nowActive) {
    originRoute.value = null;
  }
});
const currentStep = computed(() => active.value?.currentStep.value);
const progressSteps = computed(() => active.value?.progressSteps ?? []);
const currentProgressIndex = computed(() => active.value?.currentProgressIndex.value ?? -1);
const spotlightSteps = computed(() => active.value?.spotlightSteps ?? []);

const isWelcome = computed(() => {
  const id = currentStep.value?.id;
  return id === 'welcome' || id === 'fact-welcome';
});
const isComplete = computed(() => {
  const id = currentStep.value?.id;
  return id === 'complete' || id === 'fact-complete';
});
const isFacturationComplete = computed(() => currentStep.value?.id === 'fact-complete');

interface SpotlightRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

const windowWidth = ref(window.innerWidth);
const windowHeight = ref(window.innerHeight);
const spotlightRect = ref<SpotlightRect | null>(null);
const tooltipAbove = ref(false);
const isUpdating = ref(false);
let updateGeneration = 0;

const PADDING = 10;
const TOOLTIP_MARGIN = 14;

const currentSpotlightIndex = computed(() => {
  if (currentStep.value?.type !== 'spotlight') return -1;
  return spotlightSteps.value.findIndex((s) => s.id === currentStep.value?.id);
});

const isLastSpotlightStep = computed(
  () => currentSpotlightIndex.value === spotlightSteps.value.length - 1,
);

const currentDemoComponent = computed(() => {
  if (currentStep.value?.type !== 'demo') return null;
  const demoId = currentStep.value.demoId;
  return demoId ? (DEMO_REGISTRY[demoId] ?? null) : null;
});

const spotlightStyle = computed(() => {
  if (!spotlightRect.value) return {};
  const { x, y, width, height } = spotlightRect.value;
  return {
    left: `${x}px`,
    top: `${y}px`,
    width: `${width}px`,
    height: `${height}px`,
    boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.72)',
  };
});

const tooltipStyle = computed(() => {
  if (!spotlightRect.value) return {};
  const rect = spotlightRect.value;

  // Dynamic: always put the tooltip on the side with the most available space
  const SAFE_TOP = 60; // status bar + buffer
  const SAFE_BOTTOM = 130; // tab bar (66) + home indicator (up to 34) + buffer (30)

  const spaceAbove = rect.y - SAFE_TOP - TOOLTIP_MARGIN;
  const spaceBelow = windowHeight.value - (rect.y + rect.height) - SAFE_BOTTOM - TOOLTIP_MARGIN;
  const putAbove = spaceAbove > spaceBelow;

  // Minimum to always show header + a few lines + buttons (~200px)
  const maxH = Math.max(putAbove ? spaceAbove : spaceBelow, 200);

  const style: Record<string, string> = {
    left: `${TOOLTIP_MARGIN}px`,
    right: `${TOOLTIP_MARGIN}px`,
    maxHeight: `${maxH}px`,
    // overflow handled internally by flex children — no overflowY here
  };

  if (putAbove) {
    style.bottom = `${windowHeight.value - rect.y + TOOLTIP_MARGIN}px`;
  } else {
    style.top = `${rect.y + rect.height + TOOLTIP_MARGIN}px`;
  }

  return style;
});

const findElement = async (selector: string, waitForLoad = false): Promise<HTMLElement | null> => {
  const maxRetries = waitForLoad ? 12 : 4;
  const delay = waitForLoad ? 200 : 150;
  for (let i = 0; i < maxRetries; i++) {
    const el = document.querySelector(selector) as HTMLElement | null;
    if (el) return el;
    await new Promise((r) => setTimeout(r, delay));
  }
  return null;
};

// scrollIntoView natif ne fonctionne pas dans IonContent (shadow DOM Ionic).
// On utilise l'API Ionic : getScrollElement() + scrollToPoint().
// Au premier lancement après signup, l'ion-content de SignInPage reste en DOM sans
// ion-page-hidden mais avec scrollHeight=0 : il apparaissait avant celui de Dashboard
// dans querySelectorAll et faisait échouer le scroll. Quand un élément cible est
// fourni, on prend l'ion-content qui le contient — sinon fallback sur le premier
// non-caché et réellement scrollable.
function isIonPageHidden(content: Element): boolean {
  let ancestor: Element | null = content.parentElement;
  while (ancestor) {
    if (ancestor.tagName === 'ION-PAGE' && ancestor.classList.contains('ion-page-hidden')) {
      return true;
    }
    ancestor = ancestor.parentElement;
  }
  return false;
}

function findActiveIonContent(targetEl?: Element | null): Element | null {
  const contents = Array.from(document.querySelectorAll('ion-content'));
  if (targetEl) {
    const owning = contents.find((c) => c.contains(targetEl));
    if (owning && !isIonPageHidden(owning)) return owning;
  }
  for (const content of contents) {
    if (isIonPageHidden(content)) continue;
    if ((content as HTMLElement).clientHeight === 0) continue;
    return content;
  }
  return null;
}

const scrollToElement = async (el: HTMLElement) => {
  const ionContent = findActiveIonContent(el) as any;
  if (!ionContent) {
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return;
  }
  try {
    const scrollEl = (await ionContent.getScrollElement()) as HTMLElement;
    // Positionne l'élément à 28% du haut pour laisser assez de place au tooltip en dessous
    const targetRatio = 0.28;
    const y =
      el.getBoundingClientRect().top -
      scrollEl.getBoundingClientRect().top +
      scrollEl.scrollTop -
      window.innerHeight * targetRatio +
      el.offsetHeight / 2;
    await ionContent.scrollToPoint(0, Math.max(0, y), 300);
  } catch {
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
};

const updateSpotlight = async () => {
  if (!isAnyActive.value || !currentStep.value) return;

  // Chaque appel prend un jeton unique ; si un appel plus récent démarre,
  // les await intermédiaires du précédent détectent l'obsolescence et s'arrêtent.
  const gen = ++updateGeneration;
  const stale = () => gen !== updateGeneration;

  if (currentStep.value.type === 'fullscreen' || currentStep.value.type === 'demo') {
    spotlightRect.value = null;
    return;
  }

  // Retire le focus avant la navigation pour éviter le conflit aria-hidden :
  // Ionic marque la page sortante aria-hidden="true" pendant la transition,
  // ce qui déclenche un warning si un bouton du tutoriel garde le focus.
  if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur();
  }

  isUpdating.value = true;

  if (currentStep.value.tab) {
    await ionRouter.navigate(currentStep.value.tab, 'root');
    await new Promise((r) => setTimeout(r, 300));
    if (stale()) return;
    const ionContentForReset = findActiveIonContent() as any;
    if (ionContentForReset) await ionContentForReset.scrollToTop(0);
    await nextTick();
  }

  if (stale()) return;

  if (currentStep.value.type === 'page') {
    spotlightRect.value = null;
    isUpdating.value = false;
    return;
  }

  await nextTick();

  const target = currentStep.value.target;
  if (!target) {
    isUpdating.value = false;
    return;
  }

  const el = await findElement(target, currentStep.value.waitForLoad);
  if (stale()) return;

  if (!el) {
    spotlightRect.value = null;
    isUpdating.value = false;
    return;
  }

  if (currentStep.value.scrollTo) {
    await scrollToElement(el);
    if (stale()) return;
    await new Promise((r) => setTimeout(r, 350));
    if (stale()) return;
    await nextTick();
  }

  const rect = el.getBoundingClientRect();

  if (rect.width === 0 || rect.height === 0) {
    spotlightRect.value = null;
    isUpdating.value = false;
    return;
  }

  spotlightRect.value = {
    x: rect.left - PADDING,
    y: rect.top - PADDING,
    width: rect.width + PADDING * 2,
    height: rect.height + PADDING * 2,
  };

  const spaceBelow = windowHeight.value - rect.bottom;
  tooltipAbove.value = currentStep.value.tooltipPosition === 'top' || spaceBelow < 220;

  isUpdating.value = false;
};

watch([() => active.value?.currentStepIndex.value, isAnyActive], updateSpotlight);

const handleResize = () => {
  windowWidth.value = window.innerWidth;
  windowHeight.value = window.innerHeight;
  updateSpotlight();
};

onMounted(() => {
  window.addEventListener('resize', handleResize);
  window.addEventListener('orientationchange', handleResize);
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
  window.removeEventListener('orientationchange', handleResize);
});

const next = () => active.value?.next();
const prev = () => active.value?.prev();

const skip = async () => {
  const destination = originRoute.value ?? LINKS.TABS.DASHBOARD;
  await active.value?.skip();
  router.push(destination);
};

const complete = async () => {
  const isFacturation = facturation.isActive.value;
  const destination =
    originRoute.value ?? (isFacturation ? LINKS.TABS.INVOICES_BASE : LINKS.TABS.DASHBOARD);
  await active.value?.complete();
  router.push(destination);
};

const handleStartFacturation = async () => {
  await main.complete();
  facturation.start();
};
</script>

<template>
  <Transition name="t-fade">
    <div v-if="isAnyActive" class="fixed inset-0 z-[99999]" @click.stop>
      <!-- ── FULLSCREEN STEPS (welcome & complete) ── -->
      <Transition mode="out-in" name="t-slide">
        <div
          v-if="currentStep?.type === 'fullscreen'"
          :key="currentStep.id"
          class="fixed inset-0 flex items-center justify-center"
          style="
            background-image: url('/colored-background.png');
            background-size: cover;
            background-position: center top;
          "
        >
          <div class="absolute inset-0 bg-black/55" />

          <div
            class="relative z-10 flex flex-col items-center px-8 pt-safe pb-safe w-full max-w-sm overflow-y-auto"
            style="max-height: 100dvh"
          >
            <div
              class="w-28 h-28 rounded-3xl bg-white/15 backdrop-blur-sm flex items-center justify-center mb-8 mt-16"
            >
              <LocalIcon
                :name="isComplete ? 'check-circle' : 'libheros'"
                class="text-white"
                size="xlLarge"
              />
            </div>

            <p
              class="text-3xl font-bold text-center text-white mb-4 whitespace-pre-line"
              style="line-height: 1.2"
            >
              {{ currentStep.title }}
            </p>

            <p
              :class="isComplete ? 'mb-6' : 'mb-12'"
              class="text-base text-center"
              style="color: rgba(255, 255, 255, 0.8); line-height: 1.6"
            >
              {{ currentStep.description }}
            </p>

            <!-- Hint card shown only on the complete step -->
            <div
              v-if="isComplete"
              class="w-full rounded-2xl mb-6 px-4 py-3 flex items-start gap-3"
              style="background: rgba(255, 255, 255, 0.12); backdrop-filter: blur(8px)"
            >
              <div
                class="shrink-0 w-7 h-7 rounded-full flex items-center justify-center mt-0.5"
                style="background: rgba(255, 255, 255, 0.2)"
              >
                <svg
                  fill="none"
                  height="14"
                  stroke="white"
                  stroke-width="2.5"
                  viewBox="0 0 24 24"
                  width="14"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                  <line x1="12" x2="12.01" y1="17" y2="17" />
                </svg>
              </div>
              <p class="text-sm leading-relaxed" style="color: rgba(255, 255, 255, 0.85)">
                Retrouvez ces tutoriels à tout moment via le bouton
                <span class="font-semibold text-white">?</span>
                de votre profil — tutoriel général et tutoriel facturation.
              </p>
            </div>

            <div class="flex flex-col gap-3 w-full">
              <BaseButton
                :icon-name="isWelcome ? 'libheros' : undefined"
                :label="
                  isFacturationComplete
                    ? 'Revenir à l\'accueil'
                    : isComplete
                      ? 'Commencer !'
                      : 'Découvrir'
                "
                class="w-full"
                size="medium"
                type="primary"
                @click="isComplete ? complete() : next()"
              />
              <BaseButton
                v-if="isWelcome"
                class="w-full"
                label="Passer le tutoriel"
                size="medium"
                type="white"
                @click="skip"
              />
            </div>
          </div>
        </div>
      </Transition>

      <!-- ── DEMO STEPS ── -->
      <Transition mode="out-in" name="t-slide">
        <div
          v-if="currentStep?.type === 'demo'"
          :key="currentStep.id"
          class="fixed inset-0 flex flex-col"
          style="background: #f3f4f6"
        >
          <!-- MODE DÉMO banner -->
          <div
            class="flex items-center justify-center gap-2 py-2 shrink-0"
            style="background: #f59e0b"
          >
            <svg
              fill="none"
              height="14"
              stroke="white"
              stroke-width="2.5"
              viewBox="0 0 24 24"
              width="14"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" x2="12" y1="8" y2="12" />
              <line x1="12" x2="12.01" y1="16" y2="16" />
            </svg>
            <span class="text-white text-xs font-bold tracking-widest uppercase"
              >Mode Démo — Données fictives</span
            >
          </div>

          <!-- Demo component -->
          <div class="flex-1 overflow-hidden">
            <component :is="currentDemoComponent" v-if="currentDemoComponent" class="h-full" />
            <div v-else class="h-full flex items-center justify-center">
              <p class="text-gray-400 text-sm">Démo non disponible</p>
            </div>
          </div>

          <!-- Tutorial tooltip — flex column: scrollable content + sticky buttons -->
          <div
            class="bg-white border-t border-gray-100 shrink-0 flex flex-col"
            style="max-height: 42vh"
          >
            <!-- Scrollable: dots + title + description -->
            <div class="flex-1 overflow-y-auto min-h-0 px-4 pt-4">
              <div class="flex gap-1.5 justify-center mb-3">
                <div
                  v-for="(_, i) in progressSteps.length"
                  :key="i"
                  :class="i === currentProgressIndex ? 'w-5 bg-brand-solid' : 'w-2 bg-grey-200'"
                  class="h-2 rounded-full transition-[width,background-color] duration-200"
                />
              </div>
              <p class="font-semibold text-base mb-1" style="color: #111827">
                {{ currentStep.title }}
              </p>
              <p class="text-sm leading-relaxed pb-3" style="color: #6b7280">
                {{ currentStep.description }}
              </p>
            </div>

            <!-- Sticky buttons -->
            <div
              class="shrink-0 px-4 pb-safe pt-3 flex gap-2"
              style="border-top: 1px solid #f3f4f6"
            >
              <BaseButton
                v-if="currentProgressIndex > 0"
                class="flex-1"
                label="Précédent"
                size="medium"
                type="secondary"
                @click="prev"
              />
              <BaseButton
                class="flex-1"
                label="Suivant"
                size="medium"
                type="primary"
                @click="next"
              />
            </div>
          </div>
        </div>
      </Transition>

      <!-- ── PAGE STEPS (real page behind, bottom panel only) ── -->
      <template v-if="currentStep?.type === 'page'">
        <Transition mode="out-in" name="t-slide">
          <div
            v-if="currentStep"
            :key="currentStep.id"
            class="fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl flex flex-col"
            style="z-index: 100000; max-height: 40vh; box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.12)"
            @click.stop
          >
            <!-- Scrollable: dots + title + description -->
            <div class="flex-1 overflow-y-auto min-h-0 px-4 pt-4">
              <div class="flex gap-1.5 justify-center mb-3">
                <div
                  v-for="(_, i) in progressSteps.length"
                  :key="i"
                  :class="i === currentProgressIndex ? 'w-5 bg-brand-solid' : 'w-2 bg-grey-200'"
                  class="h-2 rounded-full transition-[width,background-color] duration-200"
                />
              </div>
              <p class="font-semibold text-base mb-1" style="color: #111827">
                {{ currentStep.title }}
              </p>
              <p class="text-sm leading-relaxed pb-3" style="color: #6b7280">
                {{ currentStep.description }}
              </p>
            </div>

            <!-- Sticky buttons -->
            <div
              class="shrink-0 px-4 pb-safe pt-3 flex gap-2"
              style="border-top: 1px solid #f3f4f6"
            >
              <BaseButton
                v-if="currentProgressIndex > 0"
                class="flex-1"
                label="Précédent"
                size="medium"
                type="secondary"
                @click="prev"
              />
              <BaseButton
                class="flex-1"
                label="Suivant"
                size="medium"
                type="primary"
                @click="next"
              />
            </div>
          </div>
        </Transition>
      </template>

      <!-- ── SPOTLIGHT STEPS ── -->
      <template v-if="currentStep?.type === 'spotlight'">
        <div
          v-if="spotlightRect"
          :style="spotlightStyle"
          class="fixed rounded-xl pointer-events-none"
          style="
            z-index: 99999;
            will-change: left, top, width, height;
            transition:
              left 0.28s cubic-bezier(0.4, 0, 0.2, 1),
              top 0.28s cubic-bezier(0.4, 0, 0.2, 1),
              width 0.28s cubic-bezier(0.4, 0, 0.2, 1),
              height 0.28s cubic-bezier(0.4, 0, 0.2, 1);
          "
        />
        <div v-else class="fixed inset-0 bg-black/72 pointer-events-none" style="z-index: 99999" />

        <Transition mode="out-in" name="t-pop">
          <div
            v-if="currentStep"
            :key="currentStep.id"
            :style="tooltipStyle"
            class="fixed bg-white rounded-2xl shadow-2xl flex flex-col"
            style="z-index: 100000"
            @click.stop
          >
            <!-- ── Scrollable content (header, title, description, dots) ── -->
            <div class="flex-1 overflow-y-auto min-h-0 px-5 pt-5">
              <!-- Header row: step counter + skip -->
              <div class="flex justify-between items-center mb-3">
                <span class="text-xs font-medium" style="color: #9ca3af">
                  Étape {{ currentSpotlightIndex + 1 }} sur {{ spotlightSteps.length }}
                </span>
                <button class="text-sm px-2 py-1 rounded-lg" style="color: #9ca3af" @click="skip">
                  Passer
                </button>
              </div>

              <p class="font-semibold text-base mb-1" style="color: #111827">
                {{ currentStep.title }}
              </p>

              <p
                v-if="!isUpdating || spotlightRect"
                class="text-sm leading-relaxed mb-4"
                style="color: #6b7280"
              >
                {{ currentStep.description }}
              </p>
              <div v-else class="flex items-center gap-2 mb-4" style="color: #9ca3af">
                <svg
                  class="animate-spin h-4 w-4 shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  />
                  <path class="opacity-75" d="M4 12a8 8 0 018-8v8z" fill="currentColor" />
                </svg>
                <span class="text-sm">Chargement du contenu…</span>
              </div>

              <!-- Progress dots -->
              <div class="flex gap-1.5 justify-center pb-3">
                <div
                  v-for="(_, i) in progressSteps.length"
                  :key="i"
                  :class="i === currentProgressIndex ? 'w-5 bg-brand-solid' : 'w-2 bg-grey-200'"
                  class="h-2 rounded-full transition-[width,background-color] duration-200"
                />
              </div>
            </div>

            <!-- ── Sticky navigation buttons — always visible ── -->
            <div class="shrink-0 px-5 pb-5 pt-3 flex gap-2" style="border-top: 1px solid #f3f4f6">
              <BaseButton
                v-if="currentSpotlightIndex > 0"
                class="flex-1"
                label="Précédent"
                size="medium"
                type="secondary"
                @click="prev"
              />
              <BaseButton
                class="flex-1"
                label="Suivant"
                size="medium"
                type="primary"
                @click="next"
              />
            </div>
          </div>
        </Transition>
      </template>
    </div>
  </Transition>
</template>

<style scoped>
/* Overlay global — fade rapide */
.t-fade-enter-active {
  transition: opacity 0.2s cubic-bezier(0, 0, 0.2, 1);
}
.t-fade-leave-active {
  transition: opacity 0.18s cubic-bezier(0.4, 0, 1, 1);
}
.t-fade-enter-from,
.t-fade-leave-to {
  opacity: 0;
}

/* Étapes fullscreen — enter et leave séparés pour des durées asymétriques.
   Le leave est plus court (0.15s) pour que l'entering arrive vite. */
.t-slide-enter-active {
  transition:
    opacity 0.22s cubic-bezier(0, 0, 0.2, 1),
    transform 0.22s cubic-bezier(0, 0, 0.2, 1);
}
.t-slide-leave-active {
  transition:
    opacity 0.15s cubic-bezier(0.4, 0, 1, 1),
    transform 0.15s cubic-bezier(0.4, 0, 1, 1);
}
.t-slide-enter-from {
  opacity: 0;
  transform: scale(0.97);
}
.t-slide-leave-to {
  opacity: 0;
  transform: scale(1.02);
}

/* Tooltip spotlight — pop rapide */
.t-pop-enter-active {
  transition:
    opacity 0.18s cubic-bezier(0, 0, 0.2, 1),
    transform 0.18s cubic-bezier(0, 0, 0.2, 1);
}
.t-pop-leave-active {
  transition:
    opacity 0.12s cubic-bezier(0.4, 0, 1, 1),
    transform 0.12s cubic-bezier(0.4, 0, 1, 1);
}
.t-pop-enter-from {
  opacity: 0;
  transform: translateY(6px);
}
.t-pop-leave-to {
  opacity: 0;
  transform: translateY(-3px);
}
</style>
